import { Injectable } from '@angular/core';
import { AuthService } from '../../../core/services/auth.service';
import { LanguageService } from '../../../core/services/language.service';
import { Authorities } from '../../../shared/enums/authorities.enum';
import { Modules } from '../../../shared/enums/modules.enum';
import { AlarmTypeInfo } from '../contract/alarm-type-info.contract';
import { AlarmType } from '../enums/alarm-type.enum';
import { BehaviorSubject } from 'rxjs';
import { NotificationsDatasourceService } from './notifications-datasource.service';

@Injectable({ providedIn: 'root' })
export class AlarmTypesService {
  private _alarmTypes$: BehaviorSubject<AlarmTypeInfo[]> = new BehaviorSubject<AlarmTypeInfo[]>([]);
  private _allowedAlarmTypes$: BehaviorSubject<AlarmTypeInfo[]> = new BehaviorSubject<AlarmTypeInfo[]>([]);

  constructor(
    private languageService: LanguageService,
    private authService: AuthService,
    private notificationsDataSource: NotificationsDatasourceService,
  ) {
    this._alarmTypes$.subscribe((alarmTypes) =>
      this._allowedAlarmTypes$.next(alarmTypes.filter((alarm: AlarmTypeInfo) => alarm.condition)),
    );
    this.getAlarmTypes();
  }

  private getAlarmTypes() {
    this.notificationsDataSource.isEquipmentOwner().subscribe((isOwner) =>
      this._alarmTypes$.next([
        {
          type: AlarmType.OPERATING_HOURS,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.operatingHours'),
          condition: true,
        },
        {
          type: AlarmType.OPERATING_HOURS_CONTRACT,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.contractOperatingHours'),
          condition: this.authService.hasModule(Modules.COSTS) && isOwner,
        },
        {
          type: AlarmType.TANK_FILL_LEVEL,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.tankLevel'),
          condition: this.authService.hasModule(Modules.LIVE_DATA),
        },
        {
          type: AlarmType.TOTAL_COSTS,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.totalCost'),
          condition: this.authService.hasModule(Modules.COSTS) && isOwner,
        },
        {
          type: AlarmType.DAMAGE_COSTS,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.violenceDamageCosts'),
          condition: this.authService.hasModule(Modules.COSTS) && isOwner,
        },
        {
          type: AlarmType.REPAIR_COSTS,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.repairCosts'),
          condition: this.authService.hasModule(Modules.COSTS) && isOwner,
        },
        {
          type: AlarmType.GUARANTEE,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.guarantee'),
          condition: this.authService.hasModule(Modules.SERVICE),
        },
        {
          type: AlarmType.FULL_SERVICE_CONTRACT_END,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.fullServiceContractEnd'),
          condition: this.authService.hasModule(Modules.COSTS) && isOwner,
        },
        {
          type: AlarmType.LEASING_SERVICE_CONTRACT_END,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.leasingOrRentalContractEnd'),
          condition: this.authService.hasModule(Modules.COSTS) && isOwner,
        },
        {
          type: AlarmType.GLOBAL_GEOFENCE_VIOLATION,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.globalGeofenceViolation'),
          condition:
            this.authService.hasModule(Modules.LIVE_DATA) &&
            this.authService.hasAuthority(Authorities.GEOFENCE_VIEW) &&
            this.authService.hasAuthority(Authorities.GEOFENCE_MANAGE),
        },
        {
          type: AlarmType.TIMEFENCING,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.timefence'),
          condition: this.authService.hasModule(Modules.LIVE_DATA),
        },
        {
          type: AlarmType.TRANSPORT_TASK_CREATION,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.transportCreation'),
          condition:
            this.authService.hasModule(Modules.TRANSPORT_TASK_MANAGEMENT) &&
            this.authService.hasAuthority(Authorities.TRANSPORT_TASK_DISPOSITION) &&
            isOwner,
        },
        {
          type: AlarmType.TRANSPORT_TASK_RETURNED,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.transportStatusChange', {
            value: this.translate('modules.transportation.transportState.returned'),
          }),
          condition:
            this.authService.hasModule(Modules.TRANSPORT_TASK_MANAGEMENT) &&
            this.authService.hasAuthority(Authorities.TRANSPORT_TASK_DISPOSITION) &&
            isOwner,
        },

        {
          type: AlarmType.NEW_DAMAGE_ADDED,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.newDamageAdded'),
          condition: isOwner,
        },

        {
          type: AlarmType.INACTIVE_TELEMATICS_UNIT,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.inactiveTelematicsUnit'),
          condition: true,
        },

        {
          type: AlarmType.BATTERY_VOLTAGE,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.batteryVoltage'),
          condition: this.authService.hasModule(Modules.LIVE_DATA),
        },

        {
          type: AlarmType.DEF_TANK_LEVEL,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.defTankLevel'),
          condition: this.authService.hasModule(Modules.LIVE_DATA),
        },
        {
          type: AlarmType.TELEMATICS_UNIT_BATTERY_STATUS,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.trackerBatteryStatus'),
          condition: true,
        },
        {
          type: AlarmType.THEFT_PROTECTION,
          explanation: this.translate('modules.notification.alarmRuleAddEdit.expl.theftProtection'),
          condition:
            this.authService.hasModule(Modules.LIVE_DATA) &&
            this.authService.hasModule(Modules.ALARMS) &&
            this.authService.hasModule(Modules.THEFT_PROTECTION) &&
            this.authService.hasAuthority(Authorities.GEOFENCE_VIEW),
        },
      ]),
    );
  }

  public get allowedAlarmTypes() {
    return this._allowedAlarmTypes$;
  }

  public getAllowedAlarmTypes(): BehaviorSubject<AlarmTypeInfo[]> {
    this.getAlarmTypes();
    return this._allowedAlarmTypes$;
  }

  public getExplanation(alarmTypeSearch: AlarmType): string {
    const matchingType = this._alarmTypes$.getValue().find((alarmType) => alarmType.type === alarmTypeSearch);
    return matchingType
      ? matchingType.explanation
      : this.translate('modules.notification.alarmRuleAddEdit.expl.unknown');
  }

  public isTypeAllowed(alarmType: AlarmType): boolean {
    return !!this._allowedAlarmTypes$.getValue().find((allowedType) => allowedType.type === alarmType);
  }

  private translate(key: string, interpolateParams?: object): string {
    return this.languageService.getInstant(key, interpolateParams);
  }
}
